/**
 * SchemaForm: renders a driver registry JSON Schema (a device's link_schema or
 * a point's address_schema) as a form over lib/ui (web brief U4). The schema
 * logic (field order, show-if, coercion, ajv validation) is in
 * lib/iot/schema-form; this is only the view.
 *
 * - enum → a select with the schema's x-ui-labels; integer / number → a number
 *   input with min / max and x-ui-suggestions offered in a datalist; string →
 *   a text input with the schema's first example as placeholder.
 * - `format: password` + `writeOnly` (OPC UA password) → a password input that
 *   never shows a stored value: with the secret stored (`secretsSet`), blank
 *   keeps it, and "Clear the saved password" sends "" to remove it.
 * - `choices` turns a text field into a pick list, e.g. Modbus RTU's
 *   serial_port from the ports the gateway reported in its birth.
 * - Progressive disclosure: `advanced` names optional fields (plus any the
 *   schema marks `x-ui-advanced`) that go under a collapsed "Advanced
 *   settings", opened by itself when one of them has an error; a required
 *   field is never hidden. `requiredFirst` puts the required fields first.
 */

import { useId } from "react";
import { Disclosure } from "@/lib/ui/disclosure";
import { Input } from "@/lib/ui/input";
import { NativeSelect } from "@/lib/ui/native-select";
import { cn } from "@/lib/ui/cn";
import type { JsonSchema } from "@/lib/api";
import { isFieldVisible, schemaFields, type FieldSpec, type FormValues } from "@/lib/iot/schema-form";

export interface SchemaFormProps {
  schema: JsonSchema;
  values: FormValues;
  onChange: (next: FormValues) => void;
  /** Field name → message (client validation and a 422's fields[] alike). */
  errors?: Record<string, string>;
  /** Secrets already stored on the instrument. */
  secretsSet?: readonly string[];
  /** Stored secrets the operator asked to clear. */
  clearSecrets?: ReadonlySet<string>;
  onClearSecretsChange?: (next: Set<string>) => void;
  /** Field name → the only values to offer (a select), e.g. serial ports from the birth. */
  choices?: Record<string, string[]>;
  /** Fields not to render (set elsewhere in the form). */
  omit?: readonly string[];
  disabled?: boolean;
  /** Denser layout for a per-point address editor. */
  compact?: boolean;
  /** Accessible name prefix, e.g. "Point 2" → "Point 2 Register". */
  ariaPrefix?: string;
  /** Optional fields to fold under "Advanced settings" (with any marked x-ui-advanced). */
  advanced?: readonly string[];
  /** List the required fields first (a connection form; not an address, whose order means something). */
  requiredFirst?: boolean;
}

export function SchemaForm({
  schema,
  values,
  onChange,
  errors = {},
  secretsSet = [],
  clearSecrets,
  onClearSecretsChange,
  choices,
  omit = [],
  disabled,
  compact,
  ariaPrefix,
  advanced = [],
  requiredFirst,
}: SchemaFormProps) {
  const base = useId();
  const fields = schemaFields(schema).filter((f) => !omit.includes(f.name) && isFieldVisible(schema, f.name, values));
  const set = (name: string, v: string | boolean) => onChange({ ...values, [name]: v });
  const isAdvanced = (f: FieldSpec) => !f.required && (f.advanced || advanced.includes(f.name));
  const main = fields.filter((f) => !isAdvanced(f));
  if (requiredFirst) main.sort((a, b) => Number(b.required) - Number(a.required));
  const adv = fields.filter(isAdvanced);
  const gridClass = cn("grid gap-3", compact ? "grid-cols-2 gap-2 sm:grid-cols-4" : "sm:grid-cols-2");

  const renderField = (f: FieldSpec) => {
    const id = `${base}-${f.name}`;
    const helpId = `${id}-help`;
    const error = errors[f.name];
    const stored = f.writeOnly && secretsSet.includes(f.name);
    const help = fieldHelp(f, stored, choices?.[f.name]);
    return (
      <div key={f.name} className={cn("flex flex-col gap-1", f.kind === "boolean" && "justify-end")}>
        {f.kind !== "boolean" && (
          <label htmlFor={id} className={cn("font-medium text-on-surface", compact ? "text-[11px]" : "text-xs")}>
            {f.label}
            {f.required && <span className="text-danger"> *</span>}
          </label>
        )}
        <FieldControl
          id={id}
          field={f}
          value={values[f.name]}
          onValue={(v) => set(f.name, v)}
          stored={stored}
          choices={choices?.[f.name]}
          disabled={disabled || (stored && !!clearSecrets?.has(f.name))}
          invalid={!!error}
          describedBy={help || error ? helpId : undefined}
          compact={compact}
          ariaLabel={ariaPrefix ? `${ariaPrefix} ${f.label}` : undefined}
        />
        {stored && onClearSecretsChange && (
          <label className="flex items-center gap-1.5 text-[11px] text-on-surface-muted">
            <input
              type="checkbox"
              checked={!!clearSecrets?.has(f.name)}
              disabled={disabled}
              onChange={(e) => {
                const next = new Set(clearSecrets ?? []);
                if (e.target.checked) next.add(f.name);
                else next.delete(f.name);
                onClearSecretsChange(next);
              }}
            />
            Clear the saved {f.label.toLowerCase()}
          </label>
        )}
        {(error || help) && (
          <p id={helpId} className={cn("text-[11px]", error ? "text-danger" : "text-on-surface-muted")}>
            {error ?? help}
          </p>
        )}
      </div>
    );
  };

  if (adv.length === 0) return <div className={gridClass}>{main.map(renderField)}</div>;
  const advError = adv.some((f) => !!errors[f.name]);
  return (
    <div className="flex flex-col gap-3">
      {main.length > 0 && <div className={gridClass}>{main.map(renderField)}</div>}
      <Disclosure
        summary="Advanced settings"
        hint={adv.map((f) => f.label).join(", ")}
        defaultOpen={advError}
        open={advError ? true : undefined}
        keepMounted
      >
        <div className={gridClass}>{adv.map(renderField)}</div>
      </Disclosure>
    </div>
  );
}

/** The line under a field: its description, or what a stored secret / empty choice list means. */
function fieldHelp(f: FieldSpec, stored: boolean, choices: string[] | undefined): string | undefined {
  if (stored) return "Saved. Leave blank to keep it, or type a new one to replace it.";
  if (choices && choices.length === 0) return "The gateway reported none. Type it in.";
  return f.description;
}

function FieldControl({
  id,
  field: f,
  value,
  onValue,
  stored,
  choices,
  disabled,
  invalid,
  describedBy,
  compact,
  ariaLabel,
}: {
  id: string;
  field: FieldSpec;
  value: string | boolean | undefined;
  onValue: (v: string | boolean) => void;
  stored: boolean;
  choices?: string[];
  disabled?: boolean;
  invalid: boolean;
  describedBy?: string;
  compact?: boolean;
  ariaLabel?: string;
}) {
  const size = compact ? "h-8 text-xs" : undefined;
  const common = {
    id,
    disabled,
    "aria-invalid": invalid || undefined,
    "aria-describedby": describedBy,
    "aria-label": ariaLabel,
  };
  const text = typeof value === "string" ? value : "";

  if (f.kind === "boolean") {
    return (
      <label htmlFor={id} className="flex items-center gap-2 text-xs text-on-surface">
        <input type="checkbox" {...common} checked={value === true} onChange={(e) => onValue(e.target.checked)} />
        {f.label}
      </label>
    );
  }

  if (f.kind === "enum" || (choices && choices.length > 0)) {
    const opts: Array<{ value: string; label: string }> =
      f.kind === "enum"
        ? (f.enumValues ?? []).map((e) => ({ value: String(e), label: f.enumLabels?.[String(e)] ?? String(e) }))
        : (choices ?? []).map((c) => ({ value: c, label: c }));
    // A value outside the list (an older link, a port the birth no longer lists) stays pickable.
    if (text !== "" && !opts.some((o) => o.value === text)) opts.unshift({ value: text, label: text });
    // A blank option whenever the value is empty, required or not: without it the
    // browser shows the first option while the form holds "", so a field shown
    // only for that option (x-ui-show-if, e.g. S7 `db` when area is DB) stayed
    // hidden under a select that already read "Data block (DB)".
    const blank = f.kind !== "enum" || text === "" || (!f.required && f.default === undefined);
    const blankLabel = f.kind !== "enum" ? `Pick a ${f.label.toLowerCase()}…` : f.required ? "Choose…" : "—";
    return (
      <NativeSelect {...common} className={size} value={text} onChange={(e) => onValue(e.target.value)}>
        {blank && <option value="">{blankLabel}</option>}
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </NativeSelect>
    );
  }

  const listId = f.suggestions && f.suggestions.length > 0 ? `${id}-list` : undefined;
  const numeric = f.kind === "integer" || f.kind === "number";
  return (
    <>
      <Input
        {...common}
        className={cn(size, f.kind === "text" && "font-mono")}
        type={f.kind === "password" ? "password" : numeric ? "number" : "text"}
        inputMode={f.kind === "integer" ? "numeric" : undefined}
        step={f.kind === "integer" ? 1 : numeric ? "any" : undefined}
        min={f.minimum}
        max={f.maximum}
        autoComplete={f.kind === "password" ? "new-password" : "off"}
        placeholder={
          stored
            ? "••••••••"
            : (f.example ?? (f.default !== undefined && f.default !== null ? String(f.default) : undefined))
        }
        list={listId}
        value={text}
        onChange={(e) => onValue(e.target.value)}
      />
      {listId && (
        <datalist id={listId}>
          {f.suggestions!.map((s) => (
            <option key={String(s)} value={String(s)} />
          ))}
        </datalist>
      )}
    </>
  );
}
